import React from "react";
import { Box, Typography, IconButton, useTheme, Tooltip } from "@mui/material";
import { InfoTooltip } from "../../components";
import { formatNumber } from "../../utils/formatUtils";
import { InfoOutline } from "@tiny/icons";

interface PNRatioProps {
  value: number;
  isLoading: boolean;
}

const PNRatio: React.FC<PNRatioProps> = ({ value, isLoading }) => {
  const theme = useTheme();

  const getDotColor = (val: number): string => {
    if (val < 2) return theme.palette.error.main;
    if (val < 3) return theme.palette.warning.main;
    return theme.palette.success.main;
  };

  const dotColor = getDotColor(value);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        borderRadius: 999,
        px: 1,
        py: 0.5,
        border: `0.5px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        position: "relative",
        cursor: "default",
        gap: 2,
      }}
    >
      <Tooltip
        title={`${formatNumber(value)}`}
        placement="top"
        componentsProps={{
          popper: {
            sx: {
              "& .MuiTooltip-tooltip": {
                padding: 2,
                backgroundColor: "#fff",
                color: "#000",
                boxShadow: theme.shadows[1],
              },
            },
          },
        }}
      >
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          {
            <Box
              sx={{
                width: "12px",
                height: "12px",
                borderRadius: "50%",
                backgroundColor: isLoading ? "grey.50" : dotColor,
              }}
            />
          }
          <Typography
            variant="caption"
            sx={{
              fontWeight: 500,
              fontSize: "0.75rem",
              color: isLoading ? "grey.50" : dotColor,
            }}
          >
            P/N Ratio
          </Typography>
        </Box>
      </Tooltip>
      <InfoTooltip
        message={
          <>
            <strong>P/N Ratio (Positive/Negative Ratio)</strong> – Email
            Marketing Metric
            <br />
            <br />
            <strong>Definition:</strong>
            <br />
            The P/N Ratio helps measure the effectiveness and health of your
            email campaigns by comparing positive engagement to negative
            feedback.
            <br />
            <br />
            <strong>Formula:</strong>
            <br />
            P/N Ratio = Clicks / (Spam Reports + Unsubscribes)
          </>
        }
        variant="white"
        noInfoIcon
      >
        <IconButton
          size="small"
          sx={{
            ml: 0.5,
            p: 0.25,
            color: theme.palette.text.secondary,
          }}
        >
          <InfoOutline fontSize="inherit" />
        </IconButton>
      </InfoTooltip>
    </Box>
  );
};

export default PNRatio;
